// ZONA Admin Panel — Design Tokens (TS for styled-components consumers)
// MIRROR FILE — do not edit here. Canonical source:
//   /Users/k/Zona-ads-portal/src/styles/tokens.ts
// Sync with:
//   cp /Users/k/Zona-ads-portal/src/styles/tokens.ts \
//      /Users/k/Admin-panel-new/src/styles/tokens.ts
//   (then re-add this header — the canonical file's header gets overwritten)
//
// Admin panel ALSO has _variables.scss for SCSS files. Both should be
// kept in sync with the canonical tokens.
// Brand spec: ~/.claude/projects/-Users-k-Zona/memory/reference_unified_brand_spec.md

export const colors = {
  // Backgrounds
  bg: '#050505',
  bgCanvas: '#050505',
  bgCard: '#0b0b0b',
  bgElevated: '#0f0f0f',
  bgHover: 'rgba(255, 255, 255, 0.04)',
  bgInput: 'rgba(255, 255, 255, 0.045)',
  bgInputFocus: 'rgba(255, 255, 255, 0.07)',

  // Surfaces (layered depth)
  surface1: 'rgba(255,255,255,0.035)',
  surface2: 'rgba(255,255,255,0.055)',
  surface3: 'rgba(255,255,255,0.08)',

  // Borders
  border: 'rgba(255, 255, 255, 0.06)',
  borderStrong: 'rgba(255, 255, 255, 0.11)',
  borderLight: 'rgba(255, 255, 255, 0.04)',
  borderFocus: 'rgba(114, 41, 245, 0.55)',

  // Brand
  primary: '#4F30EA',
  primaryHover: 'rgba(79, 48, 234, 0.2)',
  brand: '#7229F5',
  brandSoft: '#4F30EA',
  gradientStart: '#0A3FD6',
  gradientMid: '#7229F5',
  gradientEnd: '#7229F5',
  gradient: 'linear-gradient(135deg, #0A3FD6 0%, #7229F5 100%)',
  gradientSubtle: 'linear-gradient(135deg, rgba(10,63,214,0.18) 0%, rgba(114,41,245,0.18) 55%, rgba(114, 41, 245,0.18) 100%)',
  gradientText: 'linear-gradient(135deg, #ffffff 0%, #7229F5 100%)',

  // Hero halos — page-level radial glow stacks (apply to a -1 z-index backdrop)
  // Pair with `filter: blur(30px)` and an animation if motion is desired.
  gradientHeroHalo: 'radial-gradient(ellipse 600px 500px at 25% 20%, rgba(114,41,245,0.28) 0%, transparent 55%), radial-gradient(ellipse 600px 500px at 80% 80%, rgba(10,63,214,0.28) 0%, transparent 55%), radial-gradient(ellipse 400px 300px at 55% 50%, rgba(10, 63, 214,0.14) 0%, transparent 55%)',
  gradientHeroHaloSubtle: 'radial-gradient(ellipse 500px 350px at 15% 30%, rgba(114,41,245,0.28) 0%, transparent 55%), radial-gradient(ellipse 500px 350px at 85% 70%, rgba(10,63,214,0.22) 0%, transparent 55%)',
  gradientHeroHaloSplash: 'radial-gradient(ellipse 500px 400px at 25% 20%, rgba(114, 41, 245,0.35) 0%, transparent 55%), radial-gradient(ellipse 600px 500px at 80% 85%, rgba(10,63,214,0.35) 0%, transparent 55%), radial-gradient(ellipse 400px 300px at 55% 50%, rgba(10, 63, 214,0.18) 0%, transparent 55%)',

  // Text shimmers — pair with `background-size: 220% 220%` (or 200%) and an
  // animated background-position keyframe; clip with -webkit-background-clip: text.
  gradientShimmer: 'linear-gradient(120deg, #ffffff 0%, #7229F5 60%, #0A3FD6 100%)',
  gradientShimmerBrand: 'linear-gradient(120deg, #7229F5 0%, #0A3FD6 50%, #7229F5 100%)',

  // Text
  text: '#fff',
  textSecondary: 'rgba(255, 255, 255, 0.72)',
  textMuted: 'rgba(255, 255, 255, 0.46)',
  textFaint: 'rgba(255, 255, 255, 0.28)',

  // Status
  success: '#10B981',
  danger: '#EF4444',
  warning: '#F59E0B',
  info: '#0A3FD6',

  successBg: 'rgba(16, 185, 129, 0.1)',
  dangerBg: 'rgba(239, 68, 68, 0.08)',
  warningBg: 'rgba(245, 158, 11, 0.1)',
  infoBg: 'rgba(10, 63, 214, 0.15)',
  primaryBg: 'rgba(79, 48, 234, 0.1)',
  primaryBgStrong: 'rgba(114, 41, 245, 0.15)',
} as const;

export const radius = {
  sm: '8px',
  md: '10px',
  lg: '14px',
  xl: '18px',
  xxl: '22px',
  pill: '100px',
} as const;

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  xxl: '24px',
  xxxl: '32px',
  page: '40px',
} as const;

export const font = {
  xs: '11px',
  sm: '12px',
  md: '13px',
  base: '14px',
  lg: '15px',
  xl: '18px',
  xxl: '22px',
  xxxl: '28px',
  display: '44px',
  displayXl: '64px',
  sans: "'Inter', system-ui, sans-serif",
  ui: "'Inter', system-ui, sans-serif",
} as const;

export const shadow = {
  card: '0 1px 0 rgba(255,255,255,0.04) inset, 0 20px 40px -20px rgba(0,0,0,0.7)',
  cardLift: '0 1px 0 rgba(255,255,255,0.06) inset, 0 30px 60px -20px rgba(114,41,245,0.28), 0 20px 40px -20px rgba(0,0,0,0.7)',
  glow: '0 0 0 1px rgba(114,41,245,0.4), 0 10px 40px -8px rgba(114,41,245,0.5)',
  focusRing: '0 0 0 3px rgba(114,41,245,0.18)',
} as const;

export const motion = {
  fast: '120ms cubic-bezier(0.2, 0.8, 0.2, 1)',
  base: '200ms cubic-bezier(0.2, 0.8, 0.2, 1)',
  slow: '400ms cubic-bezier(0.2, 0.8, 0.2, 1)',
} as const;

export const breakpoint = {
  md: '768px',
  lg: '1024px',
  xl: '1280px',
} as const;
